// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dagre from 'dagre';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  CheckCircle2,
  Circle,
  Cloud,
  ArrowRight,
  GitBranch,
  HelpCircle,
  Loader2,
  Maximize2,
  MessageSquare,
  Minus,
  PlayCircle,
  Plus,
  Repeat,
  Save,
  Sparkles,
  StopCircle,
  Users,
  X,
} from 'lucide-react';
import { c } from './ui';

/* ------------------------------------------------------------ step types */
const TYPES = {
  start: { label: 'Start', accent: '#059669', tint: '#D9F2E8', ink: '#065F46', icon: PlayCircle, where: 'instructions' },
  question: { label: 'Question', accent: '#eab308', tint: '#FBF0C9', ink: '#7A5A00', icon: HelpCircle, where: 'instructions' },
  api_call: { label: 'API call', accent: '#3b82f6', tint: '#DCE8FD', ink: '#1D4ED8', icon: Cloud, where: 'functions' },
  message: { label: 'Message', accent: '#06b6d4', tint: '#D5F1F7', ink: '#0E7490', icon: MessageSquare, where: 'instructions' },
  followup: { label: 'Follow-up', accent: '#6b7280', tint: '#E3E6EB', ink: '#374151', icon: Repeat, where: 'followup' },
  human_handover: { label: 'Handover', accent: '#a855f7', tint: '#EBDDFB', ink: '#7E22CE', icon: Users, where: 'handover' },
  condition: { label: 'Condition', accent: '#f97316', tint: '#FDE3CF', ink: '#C2410C', icon: GitBranch, where: 'instructions' },
  completion: { label: 'Completion', accent: '#14b8a6', tint: '#D3F2EE', ink: '#0F766E', icon: CheckCircle2, where: 'completion' },
  end: { label: 'End', accent: '#ef4444', tint: '#FBDADA', ink: '#B91C1C', icon: StopCircle, where: 'instructions' },
  step: { label: 'Step', accent: '#94a3b8', tint: '#E6EAF1', ink: '#475569', icon: Circle, where: 'instructions' },
};
const ALIAS = { api: 'api_call', follow_up: 'followup' };
const typeKey = (t) => {
  const k = String(t || '').toLowerCase();
  const key = ALIAS[k] || k;
  return TYPES[key] ? key : 'step';
};
const WHERE_LABEL = { instructions: 'Instructions', functions: 'Functions', followup: 'Follow-ups', handover: 'Human handover', completion: 'Completion rules' };

/* ----------------------------------------------------------------- data */
const pick = (obj, keys, fallback) => {
  for (const k of keys) if (obj?.[k] !== undefined && obj?.[k] !== null && obj?.[k] !== '') return obj[k];
  return fallback;
};
const NODE_W = 220;
const NODE_H = 92;

function normalize(flow) {
  const rawNodes = Array.isArray(flow?.nodes) ? flow.nodes : [];
  const rawEdges = Array.isArray(flow?.edges) ? flow.edges : [];
  const ids = rawNodes.map((n, i) => String(pick(n, ['id', 'node_id', 'key'], `node-${i}`)));
  const steps = rawNodes.map((n, i) => ({
    id: ids[i],
    index: i + 1,
    type: typeKey(pick(n, ['type', 'node_type', 'kind', 'category'], 'step')),
    title: String(pick(n, ['title', 'label', 'name', 'step'], `Step ${i + 1}`)),
    desc: String(pick(n, ['description', 'subtitle', 'detail', 'message'], '')),
  }));
  const links = rawEdges.map((e, i) => ({
    id: String(pick(e, ['id'], `edge-${i}`)),
    source: String(pick(e, ['source', 'from', 'source_id', 'src'], ids[i] || '')),
    target: String(pick(e, ['target', 'to', 'target_id', 'dst'], ids[i + 1] || ids[i] || '')),
    label: pick(e, ['label', 'condition', 'event', 'trigger'], ''),
  }));

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 36, ranksep: 70, marginx: 20, marginy: 20 });
  steps.forEach((s) => g.setNode(s.id, { width: NODE_W, height: NODE_H }));
  links.forEach((l) => g.hasNode(l.source) && g.hasNode(l.target) && g.setEdge(l.source, l.target, { width: l.label ? Math.min(String(l.label).length, 24) * 6.4 + 28 : 0, height: l.label ? 22 : 0, labelpos: 'c' }));
  dagre.layout(g);

  const nodes = steps.map((s) => {
    const p = g.node(s.id);
    return {
      id: s.id,
      type: 'step',
      position: { x: (p?.x ?? 0) - NODE_W / 2, y: (p?.y ?? 0) - NODE_H / 2 },
      data: { ...s, selected: false, dim: false },
    };
  });
  const edges = links.map((l) => ({
    id: l.id,
    source: l.source,
    target: l.target,
    type: 'flow',
    markerEnd: { type: MarkerType.ArrowClosed, color: '#8A95AD', width: 16, height: 16 },
    data: { label: l.label, hot: false, dim: false },
  }));
  return { steps, links, nodes, edges };
}


function StepNode({ data }) {
  const t = TYPES[data.type];
  const Icon = t.icon;
  return (
    <div className={c('fvn', data.selected && 'sel', data.dim && 'dim')} style={{ width: NODE_W }}>
      <Handle type="target" position={Position.Left} className={c('fvh')} />
      <span className={c('fvi')} style={{ background: t.tint, color: t.accent }}>
        <Icon className="ags-i" />
      </span>
      <div className={c('fvt')}>
        <span className={c('fvty')} style={{ background: t.tint, color: t.ink }}>
          {t.label}
        </span>
        <b>{data.title}</b>
        {data.desc && <small>{data.desc}</small>}
      </div>
      <Handle type="source" position={Position.Right} className={c('fvh')} />
    </div>
  );
}

function FlowEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, data }) {
  const [path, lx, ly] = getSmoothStepPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius: 16 });
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{ stroke: data?.hot ? '#0F1D40' : '#8A95AD', strokeWidth: data?.hot ? 2.4 : 1.8, opacity: data?.dim ? 0.25 : 1 }}
      />
      {data?.label ? (
        <EdgeLabelRenderer>
          <div
            className={c('fvl', data?.dim && 'dim')}
            style={{ transform: `translate(-50%,-50%) translate(${lx}px,${ly}px)` }}
          >
            {String(data.label).length > 26 ? `${String(data.label).slice(0, 25)}…` : data.label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

const nodeTypes = { step: StepNode };
const edgeTypes = { flow: FlowEdge };

function Zoom() {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  return (
    <div className={c('fvz')}>
      <button type="button" aria-label="Zoom in" onClick={() => zoomIn({ duration: 150 })}>
        <Plus className="ags-i" />
      </button>
      <button type="button" aria-label="Zoom out" onClick={() => zoomOut({ duration: 150 })}>
        <Minus className="ags-i" />
      </button>
      <button type="button" aria-label="Fit to screen" onClick={() => fitView({ padding: 0.1, maxZoom: 1.1, duration: 200 })}>
        <Maximize2 className="ags-i" />
      </button>
    </div>
  );
}

function Canvas({ model, sel, onSelect, filter }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(model.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(model.edges);
  const { fitView } = useReactFlow();

  
  useEffect(() => {
    const t1 = setTimeout(() => fitView({ padding: 0.08, maxZoom: 1.1 }), 60);
    const t2 = setTimeout(() => fitView({ padding: 0.08, maxZoom: 1.1 }), 350);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  
  useEffect(() => {
    setNodes((ns) => ns.map((n) => ({ ...n, data: { ...n.data, selected: n.id === sel, dim: !!filter && n.data.type !== filter } })));
    setEdges((es) =>
      es.map((e) => {
        const src = model.steps.find((s) => s.id === e.source);
        const dst = model.steps.find((s) => s.id === e.target);
        return {
          ...e,
          data: { ...e.data, hot: e.source === sel || e.target === sel, dim: !!filter && src?.type !== filter && dst?.type !== filter },
        };
      }),
    );
  }, [sel, filter]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodeClick={(_, n) => onSelect(n.id)}
      onPaneClick={() => {}}
      fitView
      fitViewOptions={{ padding: 0.1, maxZoom: 1.1 }}
      minZoom={0.2}
      maxZoom={1.6}
      nodesConnectable={false}
      proOptions={{ hideAttribution: true }}
    >
      <Background variant="dots" gap={22} size={1.4} color="#C5CEDF" />
      <MiniMap
        pannable
        zoomable
        position="bottom-right"
        nodeColor={(n) => TYPES[n.data?.type]?.accent || '#94a3b8'}
        nodeStrokeWidth={0}
        maskColor="rgba(231,236,244,.7)"
        className={c('fvm')}
        style={{ width: 150, height: 96 }}
      />
      <Zoom />
    </ReactFlow>
  );
}


let seq = 0;
export default function FlowViewer({
  open,
  onClose,
  flow,
  agentName,
  fresh,
  generating,
  onGenerate,
  canPublish,
  saving,
  onPublish,
  onGoTo,
}) {
  const model = useMemo(() => normalize(flow), [flow]);
  const stamp = useMemo(() => ++seq, [flow]);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState('');
  const closeRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSel(model.steps[0]?.id || null);
      setFilter('');
    }
  }, [open, stamp]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const steps = model.steps;
  const step = steps.find((s) => s.id === sel) || null;
  const count = (t) => steps.filter((s) => s.type === t).length;
  const present = Object.keys(TYPES).filter((t) => t !== 'step' && count(t) > 0);
  const stat = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const from = step ? model.links.filter((l) => l.target === step.id).map((l) => ({ ...l, step: steps.find((s) => s.id === l.source) })).filter((l) => l.step) : [];
  const to = step ? model.links.filter((l) => l.source === step.id).map((l) => ({ ...l, step: steps.find((s) => s.id === l.target) })).filter((l) => l.step) : [];
  const where = step ? TYPES[step.type].where : '';

  return createPortal(
    <div className="ags">
      <div className={c('scrim show')} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className={c('fvd')} role="dialog" aria-modal="true" aria-label="Conversation flow">
          <div className={c('fvh1')}>
            <div style={{ minWidth: 0 }}>
              <h2>
                Conversation flow {agentName ? <span>· {agentName}</span> : null}
              </h2>
              {flow?.summary && <p>{flow.summary}</p>}
              {steps.length > 0 && (
                <div className={c('fvc')}>
                  <span className={c('chip')}>{stat(steps.length, 'step', 'steps')}</span>
                  {count('question') > 0 && <span className={c('chip')}>{stat(count('question'), 'question', 'questions')}</span>}
                  {count('api_call') > 0 && <span className={c('chip')}>{stat(count('api_call'), 'API call', 'API calls')}</span>}
                  {count('human_handover') > 0 && <span className={c('chip')}>{stat(count('human_handover'), 'handover to a person', 'handovers to a person')}</span>}
                  <span className={c('chip')} style={{ color: 'var(--green)' }}>{fresh ? 'Generated just now' : 'Last generated chart'}</span>
                </div>
              )}
            </div>
            <button ref={closeRef} type="button" className={c('ib')} aria-label="Close" onClick={onClose}>
              <X className="ags-i" />
            </button>
          </div>

          <div className={c('fvb')}>
            <div className={c('fvcv')}>
              {steps.length === 0 ? (
                <div className={c('fvempty')}>
                  <b>No flow steps returned.</b>
                  <span>Generate the chart again after changing your instructions or rules.</span>
                </div>
              ) : (
                <>
                  <div className={c('fvf')}>
                    <button type="button" className={c(!filter && 'on')} onClick={() => setFilter('')}>
                      All steps
                    </button>
                    {present.map((t) => (
                      <button key={t} type="button" className={c(filter === t && 'on')} onClick={() => setFilter(filter === t ? '' : t)}>
                        <i style={{ background: TYPES[t].accent }} />
                        {TYPES[t].label}
                      </button>
                    ))}
                  </div>
                  <ReactFlowProvider key={stamp}>
                    <Canvas model={model} sel={sel} onSelect={setSel} filter={filter} />
                  </ReactFlowProvider>
                </>
              )}
            </div>

            {step && (
              <aside className={c('fvs')} aria-label="Step details">
                <div className={c('fvsh')}>
                  <span className={c('fvnum')} style={{ background: TYPES[step.type].tint, color: TYPES[step.type].ink }}>
                    {step.index}
                  </span>
                  <div>
                    <small>
                      Step {step.index} of {steps.length}
                    </small>
                    <h3>{step.title}</h3>
                  </div>
                </div>
                <span className={c('fvty')} style={{ background: TYPES[step.type].tint, color: TYPES[step.type].ink }}>
                  {TYPES[step.type].label}
                </span>
                {step.desc ? <p>{step.desc}</p> : <p className={c('note')}>No extra detail for this step.</p>}

                <div className={c('fvsum')}>
                  {from.length === 0 ? 'The call starts here.' : `Comes after ${from.map((l) => l.step.title).join(' or ')}.`}{' '}
                  {to.length === 0 ? 'The call ends here.' : to.length === 1 ? `Then it moves to ${to[0].step.title}.` : `Then it moves to one of ${to.length} steps, depending on what the caller says.`}
                </div>

                <h4>Before this step</h4>
                {from.length === 0 ? (
                  <div className={c('fvk none')}>Nothing. This is where the call begins.</div>
                ) : (
                  from.map((l) => (
                    <button key={l.id} type="button" className={c('fvl2')} onClick={() => setSel(l.step.id)}>
                      <span className={c('fvnum s')}>{l.step.index}</span>
                      <span className={c('fvl2t')}>
                        <b>{l.step.title}</b>
                        <em>{l.label ? `When: ${l.label}` : 'Then continues here'}</em>
                      </span>
                      <ArrowRight className="ags-i" />
                    </button>
                  ))
                )}

                <h4>After this step</h4>
                {to.length === 0 ? (
                  <div className={c('fvk none')}>Nothing. The call ends here.</div>
                ) : (
                  to.map((l) => (
                    <button key={l.id} type="button" className={c('fvl2')} onClick={() => setSel(l.step.id)}>
                      <span className={c('fvnum s')}>{l.step.index}</span>
                      <span className={c('fvl2t')}>
                        <b>{l.step.title}</b>
                        <em>{l.label ? `If: ${l.label}` : 'Then continues here'}</em>
                      </span>
                      <ArrowRight className="ags-i" />
                    </button>
                  ))
                )}

                {onGoTo && (
                  <>
                    <h4>Change this step</h4>
                    <p className={c('note')} style={{ margin: '0 0 8px' }}>
                      The chart is built from what you wrote in {WHERE_LABEL[where]}. Edit it there, then generate the chart again.
                    </p>
                    <button
                      type="button"
                      className={c('btn small')}
                      onClick={() => {
                        onClose();
                        onGoTo(where);
                      }}
                    >
                      Open {WHERE_LABEL[where]}
                    </button>
                  </>
                )}
              </aside>
            )}
          </div>

          <div className={c('fvft')}>
            <span className={c('note')}>Drag to move around, scroll to zoom, click a step to see its details.</span>
            <span style={{ flex: 1 }} />
            {onGenerate && (
              <button type="button" className={c('btn')} onClick={onGenerate} disabled={generating}>
                {generating ? <Loader2 className="ags-i" style={{ animation: 'ags-spin 1s linear infinite' }} /> : <Sparkles className="ags-i" />}
                {generating ? 'Generating…' : 'Generate again'}
              </button>
            )}
            <button type="button" className={c('btn')} onClick={onClose}>
              Close
            </button>
            {canPublish && (
              <button
                type="button"
                className={c('btn primary')}
                disabled={saving}
                onClick={() => {
                  onClose();
                  onPublish();
                }}
              >
                {saving ? <Loader2 className="ags-i" style={{ animation: 'ags-spin 1s linear infinite' }} /> : <Save className="ags-i" />}
                {saving ? 'Saving…' : 'Publish agent'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
